import { GRACE_BOT_PHONE_NUMBER } from "./constants";
import type { ConflictRow, TranscriptionUtterance } from "./types";

function digitsOnly(value?: string | null) {
  return String(value ?? "").replace(/\D/g, "");
}

export function formatE164Phone(value?: string | null) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const digits = digitsOnly(raw);
  if (!digits) return "";
  if (digits.length === 10) return `+1${digits}`;
  if (digits.startsWith("1") && digits.length === 11) return `+${digits}`;
  if (raw.startsWith("+")) return `+${digits}`;
  return `+${digits}`;
}

export function joinPhonePath(parts: Array<string | undefined | null>) {
  return parts.map((part) => formatE164Phone(part)).filter(Boolean).join(" → ");
}

export function joinNumbersWithName(text: string, names: string[] = []) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return "";

  const parts = trimmed
    .split(/\s*(?:→|->)\s*/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return trimmed;

  const labeled = parts.map((number, index) => {
    const name = String(names[index] ?? "").trim();
    return name ? `(${name}) ${number}` : number;
  });

  return labeled.join(" → ");
}

export function getExternalTransferData(row?: ConflictRow | null) {
  const events = Array.isArray(row?.events) ? row.events : [];
  const match = events.find(
    (item) => String(item?.event ?? "") === "call.external_transferred",
  );
  return match?.data ?? null;
}

export function isOutboundCall(row?: ConflictRow | null) {
  const callType = String(row?.callType ?? "").trim().toLowerCase();
  return callType === "outgoing" || callType === "outbound";
}

export function getCallPatientName(row?: ConflictRow | null) {
  const patient = row?.patient;
  if (!patient) return "";
  return [patient.firstName, patient.lastName]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

export function getCallCaretakerName(row?: ConflictRow | null) {
  const caretaker =
    row?.rawPayload?.number?.name ??
    (row?.rawPayload as { user?: { name?: string } } | undefined)?.user?.name;
  return String(caretaker ?? "").trim();
}

export function getTranscriptParticipantLabels(row?: ConflictRow | null) {
  const patientName = getCallPatientName(row);
  const caretakerName = getCallCaretakerName(row);
  const isGraceCall = isGraceBotCall(row);

  return {
    externalLabel: patientName || "Caller",
    internalLabel: isGraceCall ? "Grace Agent" : caretakerName || "Care team",
  };
}

export function resolveTranscriptUtteranceLabel(
  utterance: TranscriptionUtterance,
  row?: ConflictRow | null,
) {
  const phone = formatE164Phone(utterance.phoneNumber);
  const labels = getTranscriptParticipantLabels(row);

  if (isCareTeamUtterance(utterance)) {
    const name = isGraceBotNumber(utterance.phoneNumber)
      ? "Grace Agent"
      : labels.internalLabel;
    return phone ? `${name} • ${phone}` : name;
  }

  const type = String(utterance.participantType || "").toLowerCase();
  if (type === "external") {
    const name = labels.externalLabel;
    return phone ? `${name} • ${phone}` : name;
  }

  return phone || type || "Speaker";
}

export function isGraceBotNumber(phone?: string | null) {
  const digits = digitsOnly(phone);
  if (!digits) return false;
  const graceDigits = digitsOnly(GRACE_BOT_PHONE_NUMBER);
  return digits === graceDigits || digits === graceDigits.slice(1);
}

export function isGraceBotCall(row?: ConflictRow | null, extraPhone?: string) {
  const events = Array.isArray(row?.events) ? row.events : [];
  const match = events.find(
    (item) => String(item?.event ?? "") === "call.external_transferred",
  );
  return match?.data?.external_caller_number ? true : false;
}

export function getConflictCallerSearchNumber(row?: ConflictRow | null) {
  const transfer = getExternalTransferData(row);
  if (isGraceBotCall(row) && transfer?.external_caller_number) {
    return formatE164Phone(transfer.external_caller_number);
  }
  return formatE164Phone(
    row?.raw_digits ??
      row?.rawDigits ??
      row?.rawPayload?.raw_digits ??
      row?.externalNumber ??
      row?.phoneNumber,
  );
}

export function formatCallCardPhone(row?: ConflictRow | null) {
  if (!row) return "—";
  const transfer = getExternalTransferData(row);
  const patient = row?.patient;
  const patientName = patient?.firstName && patient?.lastName ? `${patient.firstName} ${patient.lastName}` : "";
  const cartaker = row?.rawPayload?.number?.name ;
  const cartakerName = cartaker ? `${cartaker}` : "";
  if (isGraceBotCall(row) && transfer) {
    const gracePath = joinPhonePath([
      transfer.external_caller_number,
      transfer.number?.e164_digits,
      transfer.external_transferred_to,
    ]);

    if (gracePath) return joinNumbersWithName(gracePath, [patientName ,cartakerName , "Grace Agent"]);
  }

  const lineDigits = row.rawPayload?.number?.digits;
  const rawDigits = row.raw_digits ?? row.rawDigits ?? row.rawPayload?.raw_digits;
  const simplePath = isOutboundCall(row)
    ? joinNumbersWithName(joinPhonePath([lineDigits, rawDigits]), [cartakerName ,patientName ])
    : joinNumbersWithName(joinPhonePath([rawDigits, lineDigits]), [patientName ,cartakerName ]);

  return (
    simplePath ||
    formatE164Phone(row.externalNumber || row.phoneNumber) ||
    "—"
  );
}

export function isCareTeamUtterance(utterance: TranscriptionUtterance) {
  return (
    String(utterance.participantType || "").toLowerCase() === "internal" ||
    isGraceBotNumber(utterance.phoneNumber)
  );
}

export function normalizeUtterances(raw: unknown): TranscriptionUtterance[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((u) => ({
      startTime: typeof u?.startTime === "number" ? u.startTime : undefined,
      endTime: typeof u?.endTime === "number" ? u.endTime : undefined,
      text: u?.text != null ? String(u.text).trim() : "",
      participantType: u?.participantType ?? u?.participant_type,
      phoneNumber: u?.phoneNumber ?? u?.phone_number ?? null,
      userId: u?.userId ?? u?.user_id ?? null,
    }))
    .filter((u) => u.text);
}

export function transcriptPreview(row: ConflictRow): string {
  const utterances = row.transcriptionUtterances ?? [];
  if (utterances.length > 0) {
    const joined = utterances
      .map((u) => String(u.text ?? "").trim())
      .filter(Boolean)
      .join(" ");
    if (joined.length <= 120) return joined;
    return `${joined.slice(0, 117)}…`;
  }
  return String(row.transcript ?? "").trim();
}

export function hasTranscript(row: ConflictRow): boolean {
  return (
    (row.transcriptionUtterances?.length ?? 0) > 0 ||
    Boolean(String(row.transcript ?? "").trim())
  );
}

export function formatUtteranceTime(seconds?: number): string {
  if (seconds == null || !Number.isFinite(seconds)) return "";
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
